import { z } from 'zod/v4';
import { AuditsListQuerySchema } from './audit-list.js';

/**
 * Query for `GET /api/admin/audits` — the params the legacy
 * `GET /audits-service/v1/admin/audits` handler actually wired: `page`,
 * `count`, `sort`, `filter` (column filters; an `isArchived` key is ignored),
 * `companyId` (audited company) and `isForNetwork` (presence excludes the
 * caller's own company). Same rules as the dashboard list, so it is a pick of
 * `AuditsListQuerySchema`.
 *
 * Deliberately undeclared: `q`, `entity`, `isDropDown`, `isCompanyProfile`
 * (accepted by the legacy schema, never read), and `withAllStatus` /
 * `expirationStatus` (read by the handler but stripped by the legacy schema
 * before they arrived). The old dashboard also sends `type`, which nothing
 * ever read.
 */
export const AdminAuditsListQuerySchema = AuditsListQuerySchema.pick({
  page: true,
  count: true,
  sort: true,
  filter: true,
  companyId: true,
  isForNetwork: true,
});

export type AdminAuditsListQuery = z.infer<typeof AdminAuditsListQuerySchema>;

/**
 * One audit on the admin list — the legacy `listAdmin` SELECT plus what
 * `buildAuditFromListRow` derived: signed preview URLs in the `*FileUrl`
 * fields (raw object keys in `*OriginalUrl`), `privacyStatus`, `rights: []`
 * (the admin read joins no access-control). `isPublic` is a boolean — the
 * legacy response schema coerced it. Dates are ISO strings.
 *
 * Not carried over: the `auditFile` / `capFile` file objects, which neither
 * dashboard reads — the same call as the dashboard list.
 */
export const AdminAuditListRowSchema = z.looseObject({
  id: z.string(),
  companyId: z.string().nullable(),
  companyName: z.string().nullable(),
  city: z.string().nullable(),
  name: z.string().nullable(),
  auditTypeId: z.string().nullable(),
  auditTypeName: z.string().nullable(),
  type: z.string().nullable(),
  auditedBy: z.string().nullable(),
  auditDate: z.string().nullable(),
  referenceNumber: z.string().nullable(),
  facilityScore: z.string().nullable(),
  nextPlannedAuditDate: z.string().nullable(),
  auditReportFileId: z.string().nullable(),
  auditReportFileUrl: z.string().nullable(),
  auditReportFileOriginalUrl: z.string().nullable(),
  auditReportFileType: z.string().nullable(),
  correctiveActionPlanFileId: z.string().nullable(),
  correctiveActionPlanFileUrl: z.string().nullable(),
  correctiveActionPlanFileOriginalUrl: z.string().nullable(),
  uploaderCompanyId: z.string().nullable(),
  uploaderCompanyName: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  updatedByUserName: z.string().nullable(),
  updatedByCompanyName: z.string().nullable(),
  isPublic: z.boolean(),
  rights: z.array(z.string()),
  privacyStatus: z.object({ status: z.enum(['public', 'private']) }),
});

export type AdminAuditListRow = z.infer<typeof AdminAuditListRowSchema>;
